/* Change who does a piece of work (owner, 2026-10-09: "I dont have a way to reassign the assignment
 * within project").
 *
 * The same pickers the new-work form uses, holding the people the work has now; only what was changed
 * is sent (gateway POST /v1/work/<name>/people/). Whoever may move its dates may change its people --
 * a manager, the person who made it, its approver -- and the gateway says so if somebody else tries.
 * The change is said on the work, so its history shows who moved it to whom.
 */
import { useState } from 'react'
import { useToast } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import type { Kind, WorkRow } from './types'
import { PeoplePicker } from './Pickers'
import { whoWord } from './words'

type Body = { makers?: string[]; checker?: string; reviewer?: string; approver?: string }

/** What changed between the work's people and the form's. Pure. */
export function peopleChanges(row: Pick<WorkRow, 'assigned' | 'jd_checker' | 'jd_reviewer' | 'jd_approver'>,
                              next: { makers: string[]; checker: string; reviewer: string; approver: string }): Body {
  const body: Body = {}
  const same = (a: string[], b: string[]) => [...a].sort().join('|') === [...b].sort().join('|')
  if (!same(row.assigned ?? [], next.makers)) body.makers = next.makers
  if ((row.jd_checker ?? '') !== next.checker) body.checker = next.checker
  if ((row.jd_reviewer ?? '') !== next.reviewer) body.reviewer = next.reviewer
  if ((row.jd_approver ?? '') !== next.approver) body.approver = next.approver
  return body
}

export function PeopleChange({ row, kind, onDone, onCancel }: {
  row: WorkRow
  kind: Kind
  onDone: () => void
  onCancel: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const words = kind.words ?? {}
  const flow = kind.flow
  const [makers, setMakers] = useState<string[]>(row.assigned ?? [])
  const [checker, setChecker] = useState(row.jd_checker ?? '')
  const [reviewer, setReviewer] = useState(row.jd_reviewer ?? '')
  const [approver, setApprover] = useState(row.jd_approver ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const body = peopleChanges(row, { makers, checker, reviewer, approver })
  const makersRole = kind.roles.maker ?? kind.roles.assignee
  const showChecker = Boolean(kind.roles.checker) || Boolean(row.jd_checker)
  const showReviewer = Boolean(kind.roles.reviewer)
  const showApprover = Boolean(kind.roles.approver) || Boolean(row.jd_approver)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const done = await erp.changeWorkPeople(row.name, body)
      toast('ok', done.changed.length ? 'Changed. It is said on the work.' : 'Nothing to change.')
      onDone()
    } catch (e) {
      setError((e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="people-change" role="group" aria-label={`Change who does ${row.subject}`}>
      <PeoplePicker id={`pc_${row.name}_makers`} multiple role={makersRole}
                    label={words.makers ?? (kind.roles.maker ? whoWord('maker', words) : 'Assigned to')}
                    value={makers} onChange={setMakers} required={flow === 'review' || flow === 'complete'} />
      {showChecker ? (
        <PeoplePicker id={`pc_${row.name}_checker`} label={whoWord('checker', words)} role={kind.roles.checker}
                      value={checker ? [checker] : []} onChange={(v) => setChecker(v[0] ?? '')}
                      required={flow === 'review'} />
      ) : null}
      {showReviewer ? (
        <PeoplePicker id={`pc_${row.name}_reviewer`} label={`${whoWord('reviewer', words)} (optional)`}
                      role={kind.roles.reviewer} value={reviewer ? [reviewer] : []}
                      onChange={(v) => setReviewer(v[0] ?? '')} />
      ) : null}
      {showApprover ? (
        <PeoplePicker id={`pc_${row.name}_approver`} label={whoWord('approver', words)} role={kind.roles.approver}
                      value={approver ? [approver] : []} onChange={(v) => setApprover(v[0] ?? '')}
                      required={flow === 'review' || flow === 'decide'} />
      ) : null}
      {error ? <div className="small" role="alert" style={{ color: 'var(--danger)' }}>{error}</div> : null}
      <div className="row" style={{ gap: 6 }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !Object.keys(body).length} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-quiet btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  )
}
